import { fetcher } from "@certn/api";
import { Divider } from "@certn/cascadia-react";
import { CascadiaJsonFormContext, renderers } from "@certn/forms";
import { ajv } from "@certn/utils/src/ajv";
import { JsonFormsCore, defaultErrorTranslator } from "@jsonforms/core";
import { JsonForms } from "@jsonforms/react";
import ajvTranslateToFr from "ajv-i18n/localize/fr";
import { useField, useFormikContext } from "formik";
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";

import { ClientFormSchema } from "../../pages/order/types";
import { isAddressBasedCheck } from "../../utils/isAddressBasedCheck";

type ClientJsonFormProps = {
  isSubmitted: boolean;
  questionsUrl: string;
  clientFormPayload: Record<string, unknown>;
  initialData?: Record<string, unknown> | null | undefined;
  isApplyLink: boolean;
};

export function ClientJsonForm(props: ClientJsonFormProps) {
  const { locale } = useContext(CascadiaJsonFormContext);
  const { t } = useTranslation();
  const [field, , helpers] = useField("clientFormDataAndErrors");
  const { setFieldValue } = useFormikContext();
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const hasAppliedInitialData = useRef(false);

  const schemaCacheKey = JSON.stringify({
    questionsUrl: props.questionsUrl,
    isApplyLink: props.isApplyLink,
    clientFormPayload: props.clientFormPayload,
  });
  const {
    data: schemaData,
    isLoading,
    error,
  } = useSWR<{
    data_schema: JsonFormsCore["schema"];
    ui_schema: ClientFormSchema;
    client_form_data: Record<string, unknown>;
    check_type: string;
  }>(schemaCacheKey, () =>
    fetcher.request(props.questionsUrl, {
      method: "POST",
      body: JSON.stringify({
        check_types_with_arguments: props.clientFormPayload,
        apply_link: props.isApplyLink,
      }),
    })
  );
  const resetData = useCallback(() => {
    setFieldValue("clientFormDataAndErrors.data", {});
    setFieldValue("clientFormDataAndErrors.errors", []);
  }, [setFieldValue]);

  const clientFormData = useMemo(() => {
    return schemaData?.client_form_data || {};
  }, [schemaData]);

  useEffect(() => {
    if (!schemaData) {
      return;
    }
    const isSchemaEmpty =
      !schemaData.data_schema?.properties ||
      Object.keys(schemaData.data_schema.properties).length === 0;

    if (isSchemaEmpty) {
      resetData();
    } else if (props.initialData && !hasAppliedInitialData.current) {
      setFieldValue("clientFormDataAndErrors.data", props.initialData);
      hasAppliedInitialData.current = true;
    }
  }, [props.initialData, resetData, schemaData, setFieldValue]);

  useEffect(() => {
    const checkTypes = [
      ...Object.keys(props.clientFormPayload || {}),
      ...Object.values(props.clientFormPayload || {}).flatMap((check) => {
        const checkObj = check as { explicit_check_type?: string[] };
        return checkObj.explicit_check_type || [];
      }),
    ];

    const isUKDBSCheck = checkTypes.some((type) => type.includes("UK_DBS"));
    const isACICCheck = checkTypes.some((type) => type.includes("NCCHC"));
    const isAddressBased = Object.entries(props.clientFormPayload || {}).some(
      ([checkType, check]) => isAddressBasedCheck(checkType, check)
    );

    if (
      (isUKDBSCheck || isACICCheck || isAddressBased) &&
      Object.keys(clientFormData || {}).length > 0 &&
      !props.initialData
    ) {
      setTimeout(() => {
        setFieldValue("clientFormDataAndErrors.data", clientFormData);
      }, 0);
    }
    // eslint-disable-next-line
  }, [clientFormData]);

  if (isLoading || error || !schemaData) {
    return <></>;
  }

  if (!schemaData.ui_schema?.elements?.length) {
    return null;
  }

  return (
    <>
      {/* See CHEESE-171 and CHEESE-172 - this needs some work to make it reuse CascadiaJsonForm */}
      <CascadiaJsonFormContext.Provider
        value={{
          touched,
          setTouched(path: string) {
            helpers.setTouched(true);
            setTouched((prev) => ({ ...prev, [path]: true }));
          },
          schema: undefined!,
          setValues: () => {},
          values: field.value.data,
          submit: () => {},
          isSubmitted: props.isSubmitted,
          setFieldErrors: () => {},
          fieldErrors: {},
          setSubmitErrors: () => {},
          submitErrors: {},
        }}
      >
        <JsonForms
          ajv={ajv}
          schema={schemaData.data_schema}
          uischema={schemaData.ui_schema}
          data={field.value.data}
          onChange={helpers.setValue}
          renderers={renderers}
          i18n={{
            locale,
            translateError: (error, translate, uischema) => {
              if (
                error.keyword === "required" &&
                error.message?.startsWith("must have required property")
              ) {
                return t("is a required field");
              }
              if (locale && locale.startsWith("fr")) {
                /* ajvTranslateToFr is a function from ajv-i18n
                that takes an error message and translates it in its place */
                ajvTranslateToFr([error]);
              }
              return defaultErrorTranslator(error, translate, uischema);
            },
          }}
        />
      </CascadiaJsonFormContext.Provider>
      <Divider />
    </>
  );
}
